import React, { useState, useEffect, useCallback } from 'react';
import { backupAPI } from '../services/api';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  CloudDownload,
  Delete,
  Restore,
  Backup,
  Add,
  Refresh,
} from '@mui/icons-material';
import { formatFileSize } from '../utils/formatters';
import Button from '../components/UIKit/Button';
import { useTranslation } from 'react-i18next'; // Импортируем useTranslation для переводов

const DatabaseBackupPage = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState({ type: '', filename: '' });
  const { t } = useTranslation(['admin', 'common']); // Используем хук useTranslation с указанием пространств имен

  // Загрузить список резервных копий
  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await backupAPI.getAll();
      setBackups(response.data.backups);
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t('admin:databaseBackup.messages.fetchError')
      );
      console.error('Ошибка при загрузке списка резервных копий:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  // Создать новую резервную копию
  const handleCreateBackup = async () => {
    setLoading(true);
    try {
      await backupAPI.create();
      fetchBackups();
      setSuccess(t('admin:databaseBackup.messages.createSuccess'));
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t('admin:databaseBackup.messages.createError')
      );
      console.error('Ошибка при создании резервной копии:', err);
    } finally {
      setLoading(false);
    }
  };

  // Восстановить базу из резервной копии
  const handleRestoreBackup = async (filename) => {
    setOpenDialog(false);
    setLoading(true);
    try {
      await backupAPI.restore(filename);
      setSuccess(t('admin:databaseBackup.messages.restoreSuccess'));
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t('admin:databaseBackup.messages.restoreError')
      );
      console.error('Ошибка при восстановлении базы данных:', err);
    } finally {
      setLoading(false);
    }
  };

  // Удалить резервную копию
  const handleDeleteBackup = async (filename) => {
    setOpenDialog(false);
    setLoading(true);
    try {
      await backupAPI.delete(filename);
      fetchBackups();
      setSuccess(t('admin:databaseBackup.messages.deleteSuccess'));
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t('admin:databaseBackup.messages.deleteError')
      );
      console.error('Ошибка при удалении резервной копии:', err);
    } finally {
      setLoading(false);
    }
  };

  // Обработка загрузки файла
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Загрузить файл резервной копии
  const handleUploadBackup = async () => {
    if (!selectedFile) {
      setError(t('admin:databaseBackup.messages.selectFile'));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      await backupAPI.upload(selectedFile);
      setSuccess(t('admin:databaseBackup.messages.uploadSuccess'));
      setTimeout(() => setSuccess(null), 5000);
      setSelectedFile(null);
      fetchBackups();
      // Сбросить значение input file
      document.getElementById('backup-file-input').value = '';
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t('admin:databaseBackup.messages.uploadError')
      );
      console.error('Ошибка при загрузке файла резервной копии:', err);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Скачать файл резервной копии
  const handleDownloadBackup = async (filename) => {
    try {
      setLoading(true);
      const downloadUrl = backupAPI.getDownloadUrl(filename);

      // Используем fetch для получения файла как blob
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      // Получаем файл как blob
      const blob = await response.blob();

      // Создаем временную ссылку для скачивания
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename; // Устанавливаем имя файла
      document.body.appendChild(a);
      a.click(); // Имитируем клик для начала загрузки

      // Очищаем ресурсы
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(t('admin:databaseBackup.messages.downloadStarted'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      setError(
        t('admin:databaseBackup.messages.downloadError', {
          message: error.message,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Открыть диалоговое окно подтверждения
  const openConfirmDialog = (type, filename) => {
    setDialogAction({ type, filename });
    setOpenDialog(true);
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  return (
    <Container>
      <Box my={4}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {t('admin:databaseBackup.pageHeader')}
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {t('admin:databaseBackup.pageDescription')}
          </Typography>
        </Paper>

        <Typography variant="h4" component="h1" gutterBottom>
          {t('admin:databaseBackup.pageTitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Backup />}
              onClick={handleCreateBackup}
              disabled={loading}
              fullWidth
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t('admin:databaseBackup.create')
              )}
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Refresh />}
              onClick={fetchBackups}
              disabled={loading}
              fullWidth
            >
              {t('common:actions.refresh')}
            </Button>
          </Grid>
        </Grid>

        <Box
          mb={4}
          p={2}
          border={1}
          borderRadius={'16px'}
          borderColor="grey.300"
        >
          <Typography variant="h6" gutterBottom>
            {t('admin:databaseBackup.uploadTitle')}
          </Typography>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <Button
              variant="contained"
              component="label"
              startIcon={<Add />}
              disabled={isUploading}
            >
              {t('common:actions.chooseFile')}
              <input
                id="backup-file-input"
                type="file"
                accept=".zip"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {selectedFile && (
              <>
                <Typography variant="body2">
                  {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleUploadBackup}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('common:actions.upload')
                  )}
                </Button>
              </>
            )}
          </Box>
          {isUploading && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {t('admin:databaseBackup.listTitle')}
        </Typography>

        {loading && !isUploading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : backups.length > 0 ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ borderRadius: '16px' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('admin:databaseBackup.table.filename')}
                  </TableCell>
                  <TableCell>{t('admin:databaseBackup.table.size')}</TableCell>
                  <TableCell>
                    {t('admin:databaseBackup.table.createdAt')}
                  </TableCell>
                  <TableCell align="right">
                    {t('admin:databaseBackup.table.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.filename}>
                    <TableCell>{backup.filename}</TableCell>
                    <TableCell>{formatFileSize(backup.size)}</TableCell>
                    <TableCell>{formatDate(backup.createdAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleDownloadBackup(backup.filename)}
                        title={t('common:actions.download')}
                      >
                        <CloudDownload />
                      </IconButton>
                      <IconButton
                        color="success"
                        onClick={() =>
                          openConfirmDialog('restore', backup.filename)
                        }
                        title={t('common:actions.restore')}
                      >
                        <Restore />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() =>
                          openConfirmDialog('delete', backup.filename)
                        }
                        title={t('common:actions.delete')}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body1" color="textSecondary" align="center">
            {t('admin:databaseBackup.noBackups')}
          </Typography>
        )}
      </Box>

      {/* Диалоговое окно подтверждения */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {dialogAction.type === 'restore'
            ? t('admin:databaseBackup.confirmDialog.restore.title')
            : t('admin:databaseBackup.confirmDialog.delete.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction.type === 'restore'
              ? t('admin:databaseBackup.confirmDialog.restore.text')
              : t('admin:databaseBackup.confirmDialog.delete.text')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setOpenDialog(false)}
            color="primary"
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              dialogAction.type === 'restore'
                ? handleRestoreBackup(dialogAction.filename)
                : handleDeleteBackup(dialogAction.filename)
            }
            color={dialogAction.type === 'restore' ? 'success' : 'error'}
            autoFocus
          >
            {dialogAction.type === 'restore'
              ? t('common:actions.restore')
              : t('common:actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DatabaseBackupPage;
